// ============================================================================
// Import Executor
// Commits validated rows to the database
// ============================================================================

import { applicationRepository } from '../storage/db'
import type { JobApplication } from '../types'
import type { RowValidation } from './mapper'

export interface ImportResult {
  succeeded: number
  failed: number
  skippedDuplicates: number
  errors: { rowIndex: number; message: string }[]
}

/**
 * Import validated rows into the database
 * Skips rows marked as duplicates unless skipDuplicates is false
 */
export async function executeImport(
  validations: RowValidation[],
  options: {
    skipDuplicates: boolean
    importDuplicates: boolean
  }
): Promise<ImportResult> {
  const result: ImportResult = {
    succeeded: 0,
    failed: 0,
    skippedDuplicates: 0,
    errors: [],
  }

  for (const validation of validations) {
    // Skip invalid rows
    if (!validation.isValid) {
      result.failed++
      result.errors.push({
        rowIndex: validation.rowIndex,
        message: validation.errors.join('; '),
      })
      continue
    }

    // Handle duplicates
    if (validation.isDuplicate) {
      if (!options.importDuplicates) {
        result.skippedDuplicates++
        continue
      }
    }

    // Create the application
    try {
      const appData = buildApplicationData(validation.mappedData)
      await applicationRepository.create(appData)
      result.succeeded++
    } catch (error) {
      result.failed++
      result.errors.push({
        rowIndex: validation.rowIndex,
        message: `Database error: ${(error as Error).message}`,
      })
    }
  }

  return result
}

/**
 * Build a JobApplication-compatible creation payload from mapped data
 */
function buildApplicationData(
  mappedData: Record<string, string>
): Omit<JobApplication, 'id' | 'createdAt' | 'updatedAt'> {
  return {
    company: mappedData.company?.trim() || 'Unknown Company',
    position: mappedData.position?.trim() || 'Unknown Position',
    source: mappedData.source?.trim() || '',
    sourceUrl: mappedData.sourceUrl?.trim() || undefined,
    appliedDate: mappedData.appliedDate || new Date().toISOString().split('T')[0],
    status: 'active',
    stage: (mappedData.stage as JobApplication['stage']) || 'applied',
    notes: mappedData.notes?.trim() || '',
    contacts: [],
    followUpDate: mappedData.followUpDate?.trim() || undefined,
  }
}
